/**
 * The Supabase client — the whole of `src/data/` at N1.
 *
 * There is no token exchange and no Supabase session. Supabase validates the
 * Firebase ID token directly (third-party auth, registered per environment with
 * the Firebase project id), and the `role: "authenticated"` claim on that token
 * is what selects the Postgres role. A valid Firebase session whose token lacks
 * the claim resolves to `anon`, which holds no grants under this schema — so it
 * reads as logged out rather than erroring.
 *   https://supabase.com/docs/guides/auth/third-party/firebase-auth
 *
 * ---------------------------------------------------------------------------
 * READ THIS BEFORE WIRING REALTIME (N2)
 * ---------------------------------------------------------------------------
 * `accessToken` below is invoked **per PostgREST request**. It does NOT push a
 * new JWT onto an already-open websocket. Firebase ID tokens live ~1 hour, and
 * Supabase Realtime caches a private channel's authorization "for the duration
 * of the connection":
 *
 *   "If a new JWT is never received on the Channel, the client will be
 *    disconnected when the JWT expires."
 *   https://supabase.com/docs/guides/realtime/authorization
 *   (and docs/spikes/realtime-fanout.md:557-563, which is why the spike owes a
 *    re-run of the private-channel policy once N1 ships)
 *
 * So a realtime wave that imports this module and relies on `accessToken` alone
 * ships with a one-hour disconnect timer baked in and nothing in the file saying
 * so. `subscribeToIdToken` below is the seam that closes it: N2 calls
 * `supabase.realtime.setAuth(token)` from it. It is deliberately unused at N1 —
 * N1 opens no socket.
 */
import { createClient } from "@supabase/supabase-js";
import { getAuth, onIdTokenChanged } from "firebase/auth";
import { ensureFirebaseApp } from "../services/use-auth.js";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

// The publishable key is the ONLY key that may reach the browser. A
// `service_role` / `sb_secret_` key here is BYPASSRLS from every tab and defeats
// every control in the schema at once — CI greps the bundle and the VITE_ env
// for it (N1/S8).
let client;

/**
 * Which of the two build-time variables this bundle was built WITHOUT.
 *
 * Read at module scope because that is when Vite has already inlined them: by
 * the time anything calls getSupabase() the answer cannot change, and computing
 * it per call would suggest otherwise.
 *
 * This exists because of how the failure presented before it did. `useAuth`
 * evaluates `supabase: getSupabase()` on every render of the provider that wraps
 * the entire app, ABOVE ChunkErrorBoundary — so with the variables absent,
 * supabase-js threw `supabaseUrl is required.` and took the whole tree down. An
 * on-call engineer got a white screen and a five-word Sentry exception with no
 * environment, no variable name, and nothing to suggest that a BUILD-TIME
 * variable was the cause rather than a runtime one. The message below is the
 * whole fix: it names the variables, the mode, and the one thing that is
 * counter-intuitive about them.
 */
const MISSING_ENV = [
  SUPABASE_URL ? null : "VITE_SUPABASE_URL",
  SUPABASE_PUBLISHABLE_KEY ? null : "VITE_SUPABASE_PUBLISHABLE_KEY",
].filter(Boolean);

/**
 * The current Firebase ID token, or null when nobody is signed in.
 *
 * Exported because this bridge — "get the app, get the auth, get the user, get
 * the token, null if there is no user" — had been hand-written three times, and
 * the copies did not agree on the null case. A copy that throws instead of
 * returning null turns "your session has not loaded yet" into an exception; a
 * copy that returns `undefined` sends `Authorization: Bearer undefined`. One
 * implementation, one answer.
 *
 * Never throws. An unauthenticated page reads as `anon` rather than crashing,
 * which is what lets `getSupabase()` be called from a render.
 */
export const currentIdToken = async () => {
  const user = getAuth(ensureFirebaseApp()).currentUser;
  if (!user) return null;
  return (await user.getIdToken()) ?? null;
};

/**
 * The memoised Supabase client. One per tab: `createClient` opens no connection
 * on construction, but two clients would mean two realtime sockets in N2.
 */
export const getSupabase = () => {
  if (!client) {
    if (MISSING_ENV.length > 0) {
      const error = new Error(
        `Supabase is not configured for this build: ${MISSING_ENV.join(" and ")} ` +
          `${MISSING_ENV.length > 1 ? "are" : "is"} unset in mode "${import.meta.env.MODE}". ` +
          "These are BUILD-TIME variables — Vite inlines them into the bundle, so setting " +
          "them on the server or in a runtime secret store has no effect; they must be " +
          "present, with the VITE_ prefix, in the environment that runs the build " +
          "(the Vercel project's env vars, or the CI job). " +
          "SUPABASE_URL and NEXT_PUBLIC_SUPABASE_URL are NOT equivalents: Vite exposes " +
          "only VITE_-prefixed names to import.meta.env.",
      );
      // Named so it groups as its own Sentry issue rather than joining whatever
      // else the render happened to throw, and so `error.name` alone says
      // "configuration, not code" to whoever is paged.
      error.name = "SupabaseConfigError";
      // Deliberately NOT captured here: this throws out of a React render, so
      // Sentry's own handler captures it once with the component stack attached.
      // Capturing as well would file the same incident twice.
      throw error;
    }
    client = createClient(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
      // Returns null — never throws, never undefined — when nobody is signed
      // in, so an unauthenticated page renders as `anon` instead of crashing.
      accessToken: currentIdToken,
      // Defence in depth: a door that never answers must still surface as a
      // transport verdict rather than a promise that never settles. (PostgREST
      // v14 retried SQLSTATE 40001 forever and wrote no response at all, and
      // the edit dialog sat open with no message; 0271 fixed the door, this
      // bounds the class of failure. Wraps only the PostgREST fetch — realtime
      // and storage keep their own.) The ceiling is global — the same client
      // serves the report doors and the import staging inserts (2000-row
      // batches) — so it is set above anything measured, not at the edit
      // doors' comfort; Cloudflare would answer 524 at ~100 s regardless.
      db: { timeout: 60_000 },
    });
  }
  return client;
};

/**
 * The realtime seam N2 inherits. Registers Firebase's own token listener, which
 * fires on sign-in, sign-out and every automatic hourly refresh — the only
 * correct place to hand a fresh JWT to an open socket.
 *
 * Unused at N1 by design. N2's realtime.js calls this with
 * `(token) => supabase.realtime.setAuth(token)`.
 *
 * @param {(token: string | null) => void} onToken
 * @returns {() => void} unsubscribe
 */
export const subscribeToIdToken = (onToken) =>
  onIdTokenChanged(getAuth(ensureFirebaseApp()), async (user) => {
    onToken(user ? ((await user.getIdToken()) ?? null) : null);
  });

export default getSupabase;
